import * as React from "react";
import PropTypes from "prop-types";
import ToggleSlider from "../components/Toggle/ToggleSlider";
import KindSwitches from "./KindSwitches";
import DeviceList from "./DeviceList";
import { userNotificationSettingsGET, userNotificationSettingPUT, userPushAccessPUT, userPushSubscriptionDELETE, userPushTestPOST } from "./api";
import "./NotificationSettings.css";

// Admin: a girl's notifications, on her profile (3 Nov 2026). Push access
// (the pilot allow-list), the same per-kind switches she sees, her phones with
// remove, and Send test push. What admin cannot do is grant permission on her
// phone or sign it up: that part is hers, and the panel says whether any
// phone is on.

function errorText(e, fallback) {
	return (e && e.response && e.response.data && e.response.data.error) || fallback;
}

export default class NotificationAdminPanel extends React.Component {
	state = { settings: null, note: "", busy: false, busyKind: null, busyDevice: null, error: null, notice: null };

	componentDidMount() {
		this.load();
	}

	componentDidUpdate(prev) {
		if (prev.userId !== this.props.userId) {
			this.setState({ settings: null, error: null, notice: null }, () => this.load());
		}
	}

	load() {
		return userNotificationSettingsGET(this.props.userId)
			.then(r => this.setState({ settings: r.data, note: (r.data.access && r.data.access.note) || "" }))
			.catch(e => this.setState({ error: errorText(e, "Could not load her notification settings.") }));
	}

	setAccess(enabled) {
		this.setState({ busy: true, error: null, notice: null });
		userPushAccessPUT(this.props.userId, enabled, this.state.note)
			.then(r => this.setState({ settings: r.data, busy: false, notice: enabled ? "Push is on for this account. She turns it on for her phone from Notifications on her menu." : "Push is off for this account. Nothing more is sent to her phones." }))
			.catch(e => this.setState({ busy: false, error: errorText(e, "That didn't save.") }));
	}

	toggle(kind, push) {
		this.setState({ busyKind: kind, error: null, notice: null });
		userNotificationSettingPUT(this.props.userId, kind, push)
			.then(r => this.setState({ settings: r.data, busyKind: null }))
			.catch(e => this.setState({ busyKind: null, error: errorText(e, "That didn't save.") }));
	}

	removeDevice(d) {
		this.setState({ busyDevice: d.id, error: null, notice: null });
		userPushSubscriptionDELETE(this.props.userId, d.id)
			.then(() => this.load())
			.then(() => this.setState({ busyDevice: null }))
			.catch(e => this.setState({ busyDevice: null, error: errorText(e, "Could not remove that device.") }));
	}

	sendTest() {
		this.setState({ busy: true, error: null, notice: null });
		userPushTestPOST(this.props.userId)
			.then(r => this.setState({ busy: false, notice: r.data.sent ? "Test sent to " + r.data.sent + (r.data.sent === 1 ? " device." : " devices.") : "Nothing was sent: she has no phone that can be reached." }))
			.catch(e => this.setState({ busy: false, error: errorText(e, "The test could not be sent.") }));
	}

	render() {
		const { settings, note, busy, busyKind, busyDevice, error, notice } = this.state;
		return (
			<div>
				<h4 className="heading">Notifications</h4>
				{error && <p className="ns-error">{error}</p>}
				{notice && <p className="ns-notice">{notice}</p>}
				{!settings ? (
					!error && <p className="ns-muted">Loading…</p>
				) : (
					<div>
						<div className="ns-access">
							<button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => this.setAccess(!settings.push_access)}>
								<ToggleSlider value={settings.push_access} /> Push to her phone {settings.push_access ? "allowed" : "not allowed"}
							</button>
							<input className="form-control input-sm" placeholder="Note (why, who asked)" maxLength={300} value={note} onChange={e => this.setState({ note: e.target.value })} />
						</div>
						{!settings.configured && <p className="ns-muted">This server has no push keys yet, so nothing is sent; the settings still save.</p>}
						<h5 className="ns-group-title">Her phones</h5>
						<DeviceList devices={settings.devices} busyId={busyDevice} onRemove={d => this.removeDevice(d)} emptyText="No phone is on. She turns one on from Notifications on her menu once push is allowed." />
						{settings.push_access && settings.configured && settings.devices.some(d => !d.failed_at) && (
							<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => this.sendTest()}>
								Send test push
							</button>
						)}
						<h5 className="ns-group-title">What goes to her phone</h5>
						<KindSwitches groups={settings.groups} kinds={settings.kinds} busyKind={busyKind} onToggle={(k, v) => this.toggle(k, v)} youOrHer="her" />
					</div>
				)}
			</div>
		);
	}
}

NotificationAdminPanel.propTypes = {
	userId: PropTypes.number.isRequired
};
